/**
 * Client-SDK-specific specialization of the chat reaction document schema
 * + typed refs via `createConverter`.
 */
import {
  collection,
  doc,
  getFirestore,
  type CollectionReference,
  type DocumentReference,
  type Firestore,
} from 'firebase/firestore';
import {z} from 'zod';
import {
  StoredReactionDocSchema as StoredReactionDocBaseSchema,
  getChatReactionDocPath,
  getChatReactionsCollectionPath,
} from '../documents/chatReaction';
import type {SectionWeekId} from '../../section/sectionApi';
import {ClientTimestampSchema, createConverter} from './sdk';

/**
 * Stored reaction with client-SDK `Timestamp` instances. `updatedAt` is
 * nullable on the client side because Firestore emits local-write-pending
 * docs with unresolved `serverTimestamp()` sentinels as null — rejecting
 * those during render would trip `ChatErrorBoundary` on every optimistic
 * reaction. Persisted server shape is always populated.
 */
export const StoredReactionDocSchema = StoredReactionDocBaseSchema.extend({
  updatedAt: ClientTimestampSchema.nullable(),
}).loose();

/** Inferred type (client-SDK timestamps). */
export type StoredReactionDoc = z.infer<typeof StoredReactionDocSchema>;

/**
 * View-model for the UI: stored shape + `doc.id`. Not part of the schema;
 * attach via `{...doc.data(), id: doc.id}` at the call site (or the hook).
 */
export interface ReactionDoc extends StoredReactionDoc {
  readonly id: string;
}

const chatReactionConverter = createConverter({
  schema: StoredReactionDocSchema,
  options: {validateOnRead: true, validateOnWrite: false},
});

/**
 * Typed reference for a single reaction doc.
 *
 * @param args.sectionId - The section id.
 * @param args.sectionWeekId - The zero-indexed section week.
 * @param args.messageId - The message id being reacted to.
 * @param args.uid - The reactor's uid.
 * @param args.firestore - Firestore instance. Defaults to `getFirestore()`.
 * @returns A `DocumentReference<ReactionDoc>`.
 */
export function getChatReactionDocRef({
  sectionId,
  sectionWeekId,
  messageId,
  uid,
  firestore = getFirestore(),
}: {
  sectionId: string;
  sectionWeekId: SectionWeekId | number;
  messageId: string;
  uid: string;
  firestore?: Firestore;
}): DocumentReference<StoredReactionDoc> {
  return doc(
    firestore,
    getChatReactionDocPath({sectionId, sectionWeekId, messageId, uid}),
  ).withConverter(chatReactionConverter);
}

/**
 * Typed reference for the flat reactions collection of a given week.
 *
 * @param args.sectionId - The section id.
 * @param args.sectionWeekId - The zero-indexed section week.
 * @param args.firestore - Firestore instance. Defaults to `getFirestore()`.
 * @returns A `CollectionReference<ReactionDoc>`.
 */
export function getChatReactionsCollectionRef({
  sectionId,
  sectionWeekId,
  firestore = getFirestore(),
}: {
  sectionId: string;
  sectionWeekId: SectionWeekId | number;
  firestore?: Firestore;
}): CollectionReference<StoredReactionDoc> {
  return collection(
    firestore,
    getChatReactionsCollectionPath({sectionId, sectionWeekId}),
  ).withConverter(chatReactionConverter);
}
